import Component from '@glimmer/component';
import { action } from '@ember/object';
import type ScanModel from 'scaleflow/models/scan';

type Args = {
  params?: { scan?: ScanModel };
};

/**
 * `isArchiving` lives on the scan record, not this component: ember-table's
 * virtualized body recycles row slots across scans as the list shrinks (see
 * docs/ai-blueprints/scan-archiving.md), so local component state here would
 * end up disabling the wrong row's button mid-save.
 *
 * One cell, both directions — the same table renders the Active and Archived
 * tabs, and a row can only ever leave the list it is currently in.
 */
export default class ScansListTableArchiveActionCell extends Component<Args> {
  get isArchived(): boolean {
    return this.args.params?.scan?.archived === true;
  }

  @action
  async toggleArchived() {
    const scan = this.args.params?.scan;
    if (!scan || scan.isArchiving) return;

    const archived = !scan.archived;
    scan.isArchiving = true;
    scan.archived = archived;

    try {
      await scan.save();
    } catch {
      scan.archived = !archived;
    } finally {
      // Always cleared. An earlier version left this `true` on success, on the
      // grounds that the row was about to drop out of the list anyway and
      // re-enabling the button would only flash it. That was survivable while
      // archiving was one-way, but the record outlives the row: with an Active
      // and an Archived tab the same scan reappears on the *other* tab, and a
      // flag left set there disabled the button that would move it back.
      // The row leaves on the same tracked recompute as the save resolving, so
      // there is nothing left on screen to flash.
      scan.isArchiving = false;
    }
  }
}
